'use client';

import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { useAuiState } from '@assistant-ui/react';
import { Clock, Hourglass, RefreshCwIcon, SearchIcon } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';

import { useHitl } from '@/components/assistant-ui/hitl/hitl-provider';
import { useToolPhaseReady } from '@/components/assistant-ui/hitl/tool-phase-state';
import {
  parseHitlCallKey,
  useAwaitingHitlAction,
  useHitlCallKey,
} from '@/components/assistant-ui/hitl/use-hitl-call';
import { buildTrailTimeline } from '@/components/assistant-ui/thread/build-trail-timeline';
import { CollapsingTrail } from '@/components/assistant-ui/thread/collapsing-trail';
import { createTrailInProgressIconRenderer } from '@/components/assistant-ui/thread/rotating-trail-icon';
import {
  ElapsedTimer,
  LiveStepElapsed,
  StepElapsed,
} from '@/components/assistant-ui/thread/trail-elapsed';
import type { TimelineRow } from '@/components/assistant-ui/thread/trail-records';
import { STATUS_TRANSITION_MS } from '@/components/assistant-ui/thread/trail-step-icon';
import { deriveTimeAnchors, readCreatedAt } from '@/components/assistant-ui/thread/trail-timing';
import {
  buildPickFinal,
  resolveTrailVariants,
  safeT,
  type TrailVariantOverrides,
} from '@/components/assistant-ui/thread/trail-variants';
import { useMergedStepTimings } from '@/components/assistant-ui/thread/use-merged-step-timings';
import { useMetadataTrailTimings } from '@/components/assistant-ui/thread/use-metadata-trail-timings';
import { useStepRecords } from '@/components/assistant-ui/thread/use-step-records';
import { useTickNow } from '@/components/assistant-ui/thread/use-tick-now';
import { useToolGroup } from '@/components/assistant-ui/tool-group-provider';
import { readDestinations } from '@/components/assistant-ui/tool-groups/trail-arg-readers';
import { useConfigValue } from '@/components/providers/config-provider';
import { useDebugMode } from '@/components/providers/debug-mode-provider';
import { useReadOnly } from '@/components/providers/read-only-provider';
import { useDestinations } from '@/hooks/use-destinations';
import { useLocalizedConfig } from '@/hooks/use-localized-config';
import { useResolvedThinkingIndicatorText } from '@/hooks/use-resolved-thinking-indicator-text';
import {
  getMessageById,
  useAssistantCloudStore,
} from '@/lib/assistant-cloud-local/stores/assistant-cloud-store';
import { useLiveTrailTimings } from '@/lib/assistant-cloud-local/stores/live-trail-timings';
import type { ToolIconConfig } from '@/lib/constants/tool-ui-icons';
import { getThreadUuid } from '@/lib/utils/thread';

// The merged HITL step is one evolving row. While reading pre-approval data it shows a "fetching"
// (refresh) icon; once awaiting approval it switches to the clock/hourglass "waiting" set. The icon
// renderer remounts the rotator when the set changes (see `iconSetKey`), so the switch is clean.
const WAITING_STEP_ICONS: ToolIconConfig[] = [Clock, Hourglass];
const FETCHING_STEP_ICONS: ToolIconConfig[] = [RefreshCwIcon, SearchIcon];

export function ActivityTrail() {
  const t = useTranslations();
  const readOnly = useReadOnly();
  const { isDebugMode } = useDebugMode();
  const { getTrailConfig } = useToolGroup();

  // The trail is always on; this only reads `showInHistory`, a per-tenant
  // display tweak for read-only history views.
  const trailToggle = useConfigValue<{ showInHistory?: boolean }>('activityTrail');
  const trailConfig = useLocalizedConfig<TrailVariantOverrides>('chat.activityTrail');

  const messageId = useAuiState(({ message }) => message?.id);
  const isMessageRunning = useAuiState(({ message }) => message?.status?.type === 'running');
  const hitlModule = useHitl();
  // Use message status for suspension; the call key only tracks identity and outcome.
  const {
    toolCallId: hitlCallId,
    resultStatus: hitlStatus,
    lane: hitlLane,
  } = parseHitlCallKey(useHitlCallKey());
  const hasActiveInteraction = useAwaitingHitlAction();
  // Whether the card has finished its pre-approval fetch — drives the "fetching" → "awaiting
  // approval" phase of the merged HITL step. Approval-lane cards have no pre-fetch: always ready.
  const hitlPhaseReady = useToolPhaseReady(hitlCallId);
  const hitlLoaded = hitlLane === 'approval' || hitlPhaseReady;

  const storedMessage = useAssistantCloudStore(state =>
    messageId ? getMessageById(state.messages, messageId) : undefined,
  );
  const storeStreamDurationMs = storedMessage?.stream_duration_ms;
  const storeStepTimings = storedMessage?.step_timings;
  const storeUpdatedAtMs = readCreatedAt(storedMessage?.updated_at);

  // Prefer persisted `created_at` (backdated to the real stream start) over
  // the runtime's load-time `message.createdAt`, otherwise step offsets
  // would come out negative after a cloud rehydration.
  const rawCreatedAtMs = useAuiState(({ message }) => readCreatedAt(message?.createdAt));
  const persistedCreatedAtMs = readCreatedAt(storedMessage?.created_at);
  const createdAtMs = persistedCreatedAtMs ?? rawCreatedAtMs;

  // Fallback duration when `stream_duration_ms` was never persisted: width
  // between the backdated `created_at` and the `updated_at` set when the
  // message landed in the store. Approximate — `updated_at` advances on
  // later edits — and only meaningful when the two diverge (equal values
  // mean neither was set from a real timing signal).
  const derivedDurationMs =
    persistedCreatedAtMs !== undefined &&
    storeUpdatedAtMs !== undefined &&
    storeUpdatedAtMs > persistedCreatedAtMs
      ? storeUpdatedAtMs - persistedCreatedAtMs
      : undefined;

  // Read-only-host fallback (e.g. bazak-os-platform).
  const { stepTimings: metadataStepTimings, durationMs: metadataDurationMs } =
    useMetadataTrailTimings({ isDebugMode });

  // Captured once on mount as the fallback messageStartAt — see deriveTimeAnchors.
  const [trailMountedAt] = useState(() => Date.now());

  // Match the threadId the transport uses so the live mirror lookup hits.
  const threadIdRaw = useAuiState(
    ({ threadListItem }) =>
      (threadListItem as { remoteId?: string; id?: string } | undefined)?.remoteId ??
      (threadListItem as { id?: string } | undefined)?.id,
  );
  const threadId = threadIdRaw ? getThreadUuid(threadIdRaw) : undefined;
  const liveMirror = useLiveTrailTimings(threadId);

  const locale = useLocale();

  // Joined string keeps `useAuiState` stable across re-renders — returning
  // an array literal would re-trigger every state change. We split back to an
  // array for `useDestinations`, which itself keys on the sorted-joined codes.
  const destinationCodesKey = useAuiState(({ message }) => {
    const parts = message?.content as
      readonly { type: string; toolName?: string; args?: unknown }[] | undefined;
    if (!parts?.length) return '';
    const codes = new Set<string>();
    for (const part of parts) {
      if (part.type !== 'tool-call') continue;
      const config = part.toolName ? getTrailConfig?.(part.toolName) : undefined;
      const extracted = config?.extractCodes?.(part.args) ?? readDestinations(part.args);
      for (const code of extracted) codes.add(code);
    }
    return [...codes].sort().join(',');
  });
  const destinationCodes = useMemo(
    () => (destinationCodesKey ? destinationCodesKey.split(',') : []),
    [destinationCodesKey],
  );
  const { destinations: destinationsMap } = useDestinations(destinationCodes);

  const { stepRecords: rawStepRecords, iconsByStepId: rawIcons } = useStepRecords({
    isMessageRunning: !!isMessageRunning,
    trailConfig: trailConfig ?? undefined,
    getTrailConfig,
    t,
    locale,
    destinationsMap,
  });

  // A HITL tool is one call shown as a single evolving step ("awaiting approval" while the call
  // is unresolved, then the persisted outcome). The tenant's HITL module supplies the steps via
  // `getTrailConfig`; approval tools without a custom config keep the raw row, which already
  // carries the awaiting-approval → running → done/denied statuses via `classifyToolPart`.
  // A stable row id keeps it as ONE row that updates in place.
  const hitlGetTrailConfig = hitlModule.getTrailConfig;
  const { stepRecords, iconsByStepId } = useMemo(() => {
    if (!hitlCallId || !hitlGetTrailConfig) {
      return { stepRecords: rawStepRecords, iconsByStepId: rawIcons };
    }
    const idx = rawStepRecords.findIndex(r => r.kind === 'tool' && r.id === hitlCallId);
    if (idx === -1) return { stepRecords: rawStepRecords, iconsByStepId: rawIcons };
    const record = rawStepRecords[idx];
    const subs = hitlGetTrailConfig({
      toolName: record.toolName ?? '',
      toolCallId: hitlCallId,
      resultStatus: hitlStatus || undefined,
      awaiting: !hitlStatus,
      loaded: hitlLoaded,
      t,
    });
    if (!subs?.length) return { stepRecords: rawStepRecords, iconsByStepId: rawIcons };
    const stepId = `${hitlCallId}:hitl`;
    const records = [...rawStepRecords];
    records.splice(idx, 1, {
      id: stepId,
      kind: 'tool' as const,
      toolName: record.toolName,
      label: subs[0].label,
      status: subs[0].status,
    });
    const icons = new Map(rawIcons);
    icons.set(
      stepId,
      subs[0].icons ?? (subs[0].key === 'retrieve' ? FETCHING_STEP_ICONS : WAITING_STEP_ICONS),
    );
    return { stepRecords: records, iconsByStepId: icons };
  }, [rawStepRecords, rawIcons, hitlCallId, hitlStatus, hitlLoaded, hitlGetTrailConfig, t]);

  // Resolved = the user acted. The run keeps streaming the acknowledgement, so the trail stays
  // live — but the initial "thinking" row must not re-spin (see keepInitialThinkingComplete).
  const hitlResolved = hitlCallId !== '' && !!hitlStatus;

  const stepTimings = useMergedStepTimings({
    liveMirror,
    storeStepTimings,
    metadataStepTimings,
  });

  const totalDurationMs = storeStreamDurationMs ?? metadataDurationMs ?? derivedDurationMs;
  const requestStartedAt = liveMirror?.requestStartedAt;
  const liveFinishedAt = liveMirror?.finishedAt;

  // The transport flags `finishedAt` one frame before assistant-ui's
  // `isMessageRunning` flips false. Treating that gap as not-live avoids
  // a phantom trailing-thinking row flashing for one render at end-of-stream.
  const isLive = !!isMessageRunning && liveFinishedAt === undefined;

  // On the live→finished flip, hold the final "done" row back for one icon-crossfade cycle so the
  // preceding steps land their spinner→checkmark first — otherwise "Response finished" pops in
  // while the step above is still mid-transition. Only fires on a real transition within this mount;
  // a history render (never live) shows the final row immediately.
  //
  // BUT skip the hold when a live trailing "thinking" row was already showing: that row morphs in
  // place to the final label via its stable gap id (see build-trail-timeline), so suppressing it
  // would remove an already-visible row and flicker it out→in. The hold is only for the case where
  // the final row would be a *fresh* insertion (last real step still crossfading). `hadLiveTrailingRef`
  // holds the previous render's trailing state (updated by the effect after `rows` below); the flip
  // effect runs before it, so on the flip it reads the last live render's value.
  const [finalRowHeld, setFinalRowHeld] = useState(false);
  const wasLiveRef = useRef(isLive);
  const hadLiveTrailingRef = useRef(false);
  useEffect(() => {
    const wasLive = wasLiveRef.current;
    wasLiveRef.current = isLive;
    if (wasLive && !isLive && !hadLiveTrailingRef.current) {
      setFinalRowHeld(true);
      const timer = setTimeout(() => setFinalRowHeld(false), STATUS_TRANSITION_MS);
      return () => clearTimeout(timer);
    }
  }, [isLive]);

  // Subscribe to the 100 ms tick only while the trail is actively live.
  // History view (most messages, most of the time) sees a frozen value and
  // doesn't re-render on the interval — without this, every mounted finished
  // trail would re-render 10×/sec, fighting the expand/collapse height
  // animation (especially noticeable with React DevTools open).
  const tickNow = useTickNow({ enabled: isLive });

  const { messageStartAt, messageEndAt } = useMemo(
    () =>
      deriveTimeAnchors({
        isMessageRunning: isLive,
        trailMountedAt,
        requestStartedAt,
        createdAtMs,
        totalDurationMs,
        liveFinishedAt,
        stepTimings,
        now: tickNow,
      }),
    [
      isLive,
      trailMountedAt,
      requestStartedAt,
      createdAtMs,
      totalDurationMs,
      liveFinishedAt,
      stepTimings,
      tickNow,
    ],
  );

  const variants = useMemo(
    () => resolveTrailVariants({ overrides: trailConfig ?? undefined, t }),
    [trailConfig, t],
  );

  const fallbackPreparing =
    variants.preparing.length > 0 ? variants.preparing[0] : t('rotatingToolUI.loading');
  const personalizedThinking = useResolvedThinkingIndicatorText({ requireOverride: true });
  const thinkingLabel =
    personalizedThinking ??
    safeT({ t, key: 'activityTrail.thinking', fallback: fallbackPreparing });
  const fallbackFinalSingle = safeT({
    t,
    key: 'activityTrail.streamingDone',
    fallback: '',
  });

  const trailOverrides = trailConfig ?? undefined;
  const { resolveDoneLabel, bucketCatalogNamespace } = useToolGroup();
  const pickFinal = useMemo(
    () =>
      buildPickFinal({
        resolveDoneLabel,
        stepRecords,
        t,
        fallbackFinalSingle,
        trailOverrides,
        catalogNamespace: bucketCatalogNamespace,
      }),
    [stepRecords, t, fallbackFinalSingle, trailOverrides, resolveDoneLabel, bucketCatalogNamespace],
  );

  const { rows, totalMs } = useMemo(() => {
    const built = buildTrailTimeline({
      stepRecords,
      stepTimings,
      messageStartAt,
      messageEndAt,
      isMessageRunning: isLive,
      // Suppress the placeholder trailing "thinking" row while awaiting the user (the card is the
      // loader). NOT after they act: the acknowledgement is an LLM stream with a real gap before its
      // first token, so the trailing loader is the only progress signal during that wait. Also
      // suppress it during the post-finish hold so the final "done" row waits for the checkmarks.
      suppressTrailingRow: hasActiveInteraction || finalRowHeld,
      // After the user acts, keep the initial thinking row completed so it doesn't re-spin (the
      // HITL step has no timing, which would otherwise re-trigger it).
      keepInitialThinkingComplete: hitlResolved,
      labels: {
        thinking: thinkingLabel,
        preparing: variants.preparing,
        longWait: variants.longWait,
        pickFinal,
      },
    });
    return built;
  }, [
    stepRecords,
    stepTimings,
    messageStartAt,
    messageEndAt,
    isLive,
    hasActiveInteraction,
    hitlResolved,
    finalRowHeld,
    thinkingLabel,
    variants,
    pickFinal,
  ]);

  // Record whether this render shows a live trailing "thinking" loader as the last row.
  // The flip effect above reads the PREVIOUS render's value (it runs before this effect),
  // so on the live→done flip it sees the last live render's state and skips the hold-back
  // when that row will simply morph into the final label (no suppress → no flicker).
  useEffect(() => {
    const last = rows[rows.length - 1];
    hadLiveTrailingRef.current =
      isLive && last?.kind === 'thinking' && last?.status === 'in-progress';
  });

  // True when at least one authoritative timing source exists. Per-row and
  // total elapsed render only when this is true — avoids a misleading "0s"
  // for messages whose timings were never captured. Step count stays visible
  // regardless (it's always meaningful when rows exist).
  const hasReliableElapsed =
    !!isMessageRunning ||
    liveMirror !== undefined ||
    totalDurationMs !== undefined ||
    Object.keys(stepTimings).length > 0;

  const renderInProgressIcon = useMemo(
    () => createTrailInProgressIconRenderer(iconsByStepId),
    [iconsByStepId],
  );

  // Takes the row directly so the closure doesn't depend on `rows` — keeps
  // its reference stable across tickNow updates, which is what lets memo'd
  // TrailRow skip re-renders for unchanged rows.
  const renderStepTrailing = useMemo(() => {
    if (!isDebugMode || !hasReliableElapsed) return undefined;
    return function renderStepTrailing(row: TimelineRow): ReactNode | undefined {
      if (row.liveAnchor !== undefined) return <LiveStepElapsed anchor={row.liveAnchor} />;
      return <StepElapsed elapsedMs={row.durationMs} />;
    };
  }, [isDebugMode, hasReliableElapsed]);

  if (messageId === undefined) return null;
  if (readOnly && trailToggle?.showInHistory === false) return null;
  if (rows.length === 0) return null;

  // Step count + total elapsed are debug-only. The user-facing progress
  // signal is the loader → checkmark; raw seconds are diagnostic. Step
  // count sits at the logical start, elapsed at the end (RTL-aware via
  // `justify-between`). `px-3` matches the row's outer padding so the
  // step count aligns with the row icons above it.
  const timerNode = isDebugMode ? (
    <div className="flex items-center justify-between px-3 pt-1 pb-2 text-xs text-muted-foreground tabular-nums">
      <span>{t('activityTrail.summaryStepCount', { count: rows.length })}</span>
      {hasReliableElapsed ? <ElapsedTimer elapsedMs={totalMs} isLive={isLive} /> : null}
    </div>
  ) : null;

  return (
    <CollapsingTrail
      rows={rows}
      isLive={isLive}
      renderInProgressIcon={renderInProgressIcon}
      renderStepTrailing={renderStepTrailing}
      timerNode={timerNode}
    />
  );
}
